import { Step } from "common/components/StepContainer";
import { RootState } from "RootReducer";
import { transferCreateChooseShippingConfig } from "./actions";
import { selectTransferCreate } from "./store/TransferCreateSelectors";
import { SharedConfirmedStep } from "common/wholesale-common/components/SharedConfirmedStep";
import { transfersShipmentCreateLabels } from "./transfersShipmentCreate.labels";
import { shippingPartnerToWholesaleShippingMethod } from "./steps/common/replenishment/useReplenishmentOrderShippingStep";
import { DistributionChannel, OrderResponse, ShippingPartner } from "@deliverr/replenishment-client";
import { ReplenishmentOrderConfirmation } from "./steps/common/replenishment/confirm/ReplenishmentOrderConfirmation";
import { TransferSelectPrep } from "prep/create/TransferSelectPrep";
import { withNextLoader } from "./steps/common/replenishment/utils";
import { TransferSelectDestinationFcStep } from "./steps/ecom/TransferSelectDestinationFcStep";
import { TransferSelectPrepInstructions } from "./steps/ecom/TransferSelectPrepInstructions";
import { PREP_OPTIONS_WITH_UNIT_BARCODING } from "./steps/ecom/constants";
import { fetchPrepJob } from "prep/details/fetchPrepDetails";
import { createOrUpdatePrepInstructions } from "./actions/replenishmentOrder/createOrUpdatePrepInstructions";
import { resetPrepInstructions } from "./actions/replenishmentOrder/resetPrepInstruction";
import { TransferUnifiedProductSelectionStep } from "./steps/common/TransferUnifiedProductSelectionStep";
import { saveDraftReplenishmentOrder } from "./steps/common/actions/saveDraftReplenishmentOrder";
import { WholesaleShipmentType } from "@deliverr/wholesale-client";
import { isEmpty } from "lodash";
import { confirmDraftReplenishmentOrder } from "./steps/common/actions/confirmDraftReplenishmentOrder";

export enum EcomV2Step {
  SelectProductAndQuantities = "ecom/v2/selectProductAndQuantities",
  SelectDestinationFC = "ecom/v2/selectDestinationFC",
  SelectPrep = "ecom/v2/selectPrep",
  SelectPrepInstructions = "ecom/v2/selectPrepInstructions",
  Confirm = "ecom/v2/confirm",
  Confirmed = "ecom/v2/confirmed",
}

export const ecomV2Steps: (dispatch, getState: () => RootState) => Record<EcomV2Step, Step> = (dispatch, getState) => ({
  [EcomV2Step.SelectProductAndQuantities]: {
    component: TransferUnifiedProductSelectionStep,
    next: withNextLoader(
      async () => {
        const state = getState();
        const { distributionChannel, isEachPickingOrder } = selectTransferCreate(state);

        if (await dispatch(saveDraftReplenishmentOrder())) {
          dispatch(
            transferCreateChooseShippingConfig({
              shippingMethod: shippingPartnerToWholesaleShippingMethod[ShippingPartner.DELIVERR],
              shipmentType: WholesaleShipmentType.PARTIAL_PALLET,
            })
          );

          if (distributionChannel === DistributionChannel.ECOM_DIRECT_TO_FC) {
            return EcomV2Step.SelectDestinationFC;
          } else if (!isEachPickingOrder) {
            // TODO - Remove isEachPickingOrder check when prep is supported for each picking orders
            return EcomV2Step.SelectPrep;
          } else {
            return EcomV2Step.Confirm;
          }
        }
        return;
      },
      dispatch,
      getState
    ),
  },
  [EcomV2Step.SelectDestinationFC]: {
    component: TransferSelectDestinationFcStep,
    next: withNextLoader(
      async () => {
        const state = getState();
        const { isEachPickingOrder } = selectTransferCreate(state);

        if (await dispatch(saveDraftReplenishmentOrder())) {
          return !isEachPickingOrder ? EcomV2Step.SelectPrep : EcomV2Step.Confirm;
        }
        return;
      },
      dispatch,
      getState
    ),
  },
  [EcomV2Step.SelectPrep]: {
    component: TransferSelectPrep,
    next: withNextLoader(
      async () => {
        const { prepOptions } = selectTransferCreate(getState());

        if (isEmpty(prepOptions)) {
          return EcomV2Step.Confirm;
        }

        const replenishmentOrder: OrderResponse | undefined = await dispatch(saveDraftReplenishmentOrder());

        if (replenishmentOrder) {
          const hasBarcodingPrep = prepOptions?.some((option) => PREP_OPTIONS_WITH_UNIT_BARCODING.includes(option));

          const shipment = replenishmentOrder?.shipments?.[0];
          const dpId = shipment?.dpId;

          if (hasBarcodingPrep && dpId) {
            await dispatch(fetchPrepJob(dpId));
            return EcomV2Step.SelectPrepInstructions;
          }

          if (dpId && (await resetPrepInstructions(dispatch, dpId))) {
            return EcomV2Step.Confirm;
          }
        }

        return;
      },
      dispatch,
      getState
    ),
  },
  [EcomV2Step.SelectPrepInstructions]: {
    component: TransferSelectPrepInstructions,
    next: withNextLoader(
      async () => {
        if (await createOrUpdatePrepInstructions(dispatch, getState)) {
          return EcomV2Step.Confirm;
        } else {
          return EcomV2Step.SelectPrepInstructions;
        }
      },
      dispatch,
      getState
    ),
  },
  [EcomV2Step.Confirm]: {
    component: ReplenishmentOrderConfirmation,
    next: withNextLoader(
      async () => {
        if (await dispatch(confirmDraftReplenishmentOrder())) {
          return EcomV2Step.Confirmed;
        }
        return;
      },
      dispatch,
      getState
    ),
  },
  [EcomV2Step.Confirmed]: {
    component: SharedConfirmedStep(transfersShipmentCreateLabels),
    hasNoBack: true,
  },
});
